import { Affix } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import WhiteKeyIcon from 'assets/Icons/WhiteKeyIcon';
import clsx from 'clsx';
import * as cookie from 'cookie';
import { AnimatePresence } from 'framer-motion';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { useSessionTracking } from '@/hooks/useSessionTracking';

import GenuineProduct from '@/components/BookLandingPageComp/GenuineProduct';
import PublicationDetails from '@/components/BookLandingPageComp/PublicationDetails';
import Banner from '@/components/Global/Banner';

import { getGlobalCountry } from '@/atoms/countryState';
import { detectCountry, getActiveCountries } from '@/services/globalCountry';
import { checkIsPurchased } from '@/services/ReviewLandingPage';
import { productAccessCheckSSR } from '@/services/Subscribe';
import { setEventContext } from '@/utils/eventContext';
import { EventV2 } from '@/utils/Events/events';
import {
  hasPreviouslySubscribed,
  isPlatformSubscriptionActive,
  isSubscriptionActive,
} from '@/utils/index';
import { IProduct } from '@/utils/interfaces/Product';
import { IProductAccessCheck } from '@/utils/interfaces/Subscription';
import { formatPrice } from '@/utils/Product';
import { removeTrailingSlash } from '@/utils/removeTrailingSlash';

import { authModalControl, tokenUpdate } from '../../../atoms/authModalControl';
import { dynamicTrackingParamsState } from '../../../atoms/dynamicTrackingParams';
import { localUserID, user } from '../../../atoms/user';
import AuthorDetailsSection from '../../../components/BookLandingPageComp/AuthorDeatils';
import BookDescription from '../../../components/BookLandingPageComp/BookDescription';
import CurriculumSection from '../../../components/BookLandingPageComp/CurriculumSection';
import HeroSection from '../../../components/BookLandingPageComp/HeroSection';
import IframeLandingPage from '../../../components/BookLandingPageComp/IframeLandingPage';
import RecommendedAndOverViewSection from '../../../components/BookLandingPageComp/RecommendedAndOverViewSection';
import RecommendedBooksSection from '../../../components/BookLandingPageComp/RecommendedBooksSection';
import ReviewsLandingPage from '../../../components/BookLandingPageComp/ReviewsLandingPage';
import SlidBookContainer from '../../../components/BookLandingPageComp/SlidBookContainer';
import SneakPeekSection from '../../../components/BookLandingPageComp/SneakPeekSection';
import Products from '../../../components/ProjectPage/Products';
import ButtonDefault, {
  ButtonVariants,
} from '../../../components/shared/basic/button';
import Section from '../../../components/shared/basic/section';
import { ShouldRender } from '../../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../../components/shared/formateErrorObject';
import { getLoginToken } from '../../../components/shared/getToken';
import {
  extractAuthorDetailsData,
  extractCurriculumLandingPageData,
  extractHeroSectionData,
  extractIBookDescriptionData,
  extractIframeData,
  extractRecommendedAndOverViewSectionData,
  extractSlidBookContainerData,
  extractSneakPeekData,
} from '../../../components/shared/LandingPageHelper';
import CustomLoader from '../../../components/shared/Loading';
import { notify } from '../../../components/shared/notify';
import { Endpoints } from '../../../network';
import {
  addCouponCode,
  addToCart,
  addToRecentlyViewed,
} from '../../../services/Cart';
import {
  getBooDataByIdSSR,
  getBookLandingPageData,
  getBookLandingPageDataSSR,
  getLandingPageSeoSSR,
  getProductDataSSR,
} from '../../../services/LandingPage';
import { getBookBySlug } from '../../../services/Library/Books';
import { getAuthorProfileById } from '../../../services/School';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import {
  IAuthorDetails,
  IAuthorDetailsList,
  IBookDescription,
  IBookLandingPage,
  ICurriculumLandingPageData,
  IHeroSectionLandingPage,
  IIframeLandingPage,
  IRecommendedAndOverViewSection,
  ISlidBook,
  ISneakPeek,
} from '../../../utils/interfaces/landingPage';
import { IBookDetails } from '../../../utils/interfaces/Products/books';
import { User } from '../../../utils/interfaces/User';

interface Props {
  metaData: {
    title: string;
    description: string;
    author: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
    categories?: [
      {
        _id: string;
        name: string;
      },
    ];
    canonicalUrl?: string;
    slug: string;
    price: number;
    reviews: any[];
    ratingValue: number;
    reviewCount: number;
  } | null;
  canonicalUrlPageUrl?: string;
  digitalCopyCountryData?: any;
  hardCopyCountryData?: any;
  isPurchased: boolean;
  lastPageRead: number | null;
  productAccessCheckData: IProductAccessCheck | null;
  landingPageData: any | null;
  ecomProductData?: IProduct | null;
}

export interface IAuthorInterface {
  follower_count: string;
  user_name: string;
  is_followed?: boolean;
  is_verified?: boolean;
}

export const generateUrlForImage = (
  baseUrl: string,
  endpoint: string,
  id: string,
): string => {
  const encodedId = encodeURIComponent(id);
  const url = `${baseUrl}${endpoint}${encodedId}`;
  return url;
};

const BookDetailsPage: React.FC<Props> = ({
  metaData,
  canonicalUrlPageUrl,
  digitalCopyCountryData,
  hardCopyCountryData,
  isPurchased,
  lastPageRead,
  productAccessCheckData,
  landingPageData,
  ecomProductData,
}) => {
  console.log({ landingPageData })
  console.log({ ecomProductData })
  const isOrange = true; // Controls orange theme across all child components

  const [bookLandingDetails, setBookLandingDetails] =
    useState<IBookLandingPage>();
  const [loading, setLoading] = useState(false);

  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });

  const [token, setToken] = useState('');
  const [offset, setOffset] = useState<number>(0);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [correctedSlug, setCorrectedSlug] = useState<string>();
  const [authorsData, setAuthorsData] = useState<
    IAuthorInterface | IAuthorInterface[]
  >();
  const [bookDetails, setBooksDetails] = useState<IBookDetails>();
  const [, setVisible] = useRecoilState(authModalControl);
  const [lastFetchedBookId, setLastFetchedBookId] = useState<string | null>(
    null,
  );

  const [purchasedData, setPurchasedData] = useState<{
    hardProduct: {
      purchased: boolean;
    };
    softProduct: {
      purchased: boolean;
    };
  }>({
    hardProduct: {
      purchased: false,
    },
    softProduct: {
      purchased: false,
    },
  });

  const router = useRouter();
  const screens = useBreakpoint();
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);
  const setDynamicTrackingParams = useSetRecoilState(
    dynamicTrackingParamsState,
  );

  const { bookId, swp, short } = router.query;

  const isPreOrderAvailable = useMemo(() => {
    return (
      hardCopyCountryData?.is_preorder &&
      hardCopyCountryData?.is_available !== null
    );
  }, [hardCopyCountryData]);

  const claimHandle = (productId: number) => {
    router.push(`/subscription-checkout?productId=${productId}`);
  };

  const checkIsPurchasedHandle = async () => {
    try {
      if (!bookLandingDetails) return;

      const hard = await checkIsPurchased(
        bookLandingDetails?.hardCoverProductId?.toString(),
      );
      setPurchasedData((prevState) => ({
        ...prevState,
        hardProduct: {
          purchased: hard,
        },
      }));
      const soft = await checkIsPurchased(
        bookLandingDetails?.digitalProductId.toString(),
      );
      setPurchasedData((prevState) => ({
        ...prevState,
        softProduct: {
          purchased: soft,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const getBookDetailsById = async (bookId: string) => {
    try {
      setLoading(true);

      let data = landingPageData;

      if (!data) {
        const response = await getBookLandingPageData(bookId);
        data = response?.data;
      }

      if (data?.correctedSlug) {
        setCorrectedSlug(data.correctedSlug);
      }

      setBookLandingDetails({
        ...data?.form,
        bookType: data?.bookType,
        categories: data?.categories?.map((category: any) => ({
          categoryId: category._id,
          name: category.name,
        })),
      });

      if (data?.bookType === 'short' && short !== 'true') {
        router.push(`${router.asPath.split('?')[0]}?short=true`, undefined, {
          shallow: true,
        });
      }
    } catch (error: any) {
      console.error('API call error:', error);

      if (error?.response?.status === 404) {
        router.push('/books');
      }
    } finally {
      setLoading(false);
    }
  };

  const getBookDetailsByIdSwp = async (bookId: string, userId?: string) => {
    try {
      setLoading(true);

      const response = await getBookBySlug(bookId, userId && userId);

      const formateData = {
        authorName: response?.authors?.[0],
        bookName: response?.title,
        desc: response?.description,
        lang: 'English',
        type: 'Book',
        _id: response?._id,
        authorAvatar: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}`,
          `${Endpoints.getAuthorAvatar}/`,
          `${response?.author_list?.[0]?._id}`,
        ),
        totalPages: response?.totalPages,
        bookLSample: response?._id,
        bookType: response?.type,
        isSubscribed: response?.subscribe?.subscribed || false,
        image: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${response?._id}`,
        ),
      };
      setBooksDetails(formateData);
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateOffset = () => {
      const containerHeight =
        document.querySelector('.slid-book-container')?.clientHeight || 0;
      const screenHeight = window.innerHeight;
      setOffset((screenHeight - containerHeight) / 15);
    };

    updateOffset();

    window.addEventListener('resize', updateOffset);

    return () => {
      window.removeEventListener('resize', updateOffset);
    };
  }, [bookId]);

  useEffect(() => {
    if (bookId && userData) {
      if (swp === 'false') {
        getBookDetailsByIdSwp(bookId as string, userData && userData?.zl_uid);
      }
    }
  }, [userData, bookId]);

  useEffect(() => {
    if (bookId && bookId !== lastFetchedBookId) {
      if (swp === 'false') {
        getBookDetailsByIdSwp(bookId as string);
      } else {
        getBookDetailsById(bookId as string);
      }

      setLastFetchedBookId(bookId as string);
    }
  }, [bookId, userData, token, short]);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  useEffect(() => {
    if (ecomProductData?.display_name) {
      setDynamicTrackingParams({ source_name: ecomProductData.display_name });
      setEventContext({ source_name: ecomProductData?.display_name });
    }
  }, [ecomProductData?.display_name, setDynamicTrackingParams]);

  useEffect(() => {
    if (landingPageData?.categories?.[0]?.name) {
      setEventContext({
        product_category: landingPageData?.categories?.[0]?.name,
      });
    }
  }, [landingPageData?.categories?.[0]?.name]);

  useEffect(() => {
    const baseSlug = `/oranges/${bookLandingDetails?.productSlug}-${bookLandingDetails?.readersProductId}`;
    const queryParams = short === 'true' ? '?short=true' : '';
    const formattedSlug = `${baseSlug}${queryParams}`;

    if (
      correctedSlug &&
      bookLandingDetails?.readersProductId &&
      (router.pathname?.split('?')[0] || '') !== baseSlug
    ) {
      router.replace(formattedSlug, undefined, { shallow: true });
    }
  }, [correctedSlug, bookLandingDetails, short]);

  useEffect(() => {
    checkIsPurchasedHandle();
  }, [bookLandingDetails]);

  const keyCount = useMemo(() => {
    return (
      userData?.features?.reduce(
        (sum, i) => sum + (i?.available_quantity || 0),
        0,
      ) || 0
    );
  }, [userData]);

  const handleApplyCoupon = async (coupon: string) => {
    try {
      await addCouponCode(coupon as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    _type?: 'hardCopy' | 'digitalCopy',
    isBuyNow?: boolean,
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      const data = await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${slidBookContainerData?.bookName} added to cart successfully!`,
        'success',
      );
      if (isBuyNow) {
        await handleApplyCoupon(
          hardCopyCountryData?.preorder_coupon_code || '',
        );
        router.push(
          `${process.env.NEXT_PUBLIC_CHECKOUT_ROUTE || '/checkout'}?cartId=${data?.data?.cart_id}&pre`,
        );
      } else {
        router.push(`/cart/`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  const heroSectionData: IHeroSectionLandingPage | undefined =
    bookLandingDetails ? extractHeroSectionData(bookLandingDetails) : undefined;

  const slidBookContainerData: ISlidBook | undefined = bookLandingDetails
    ? extractSlidBookContainerData(bookLandingDetails)
    : undefined;

  const bookDescriptionData: IBookDescription | undefined = bookLandingDetails
    ? extractIBookDescriptionData(bookLandingDetails)
    : undefined;

  const recommendedAndOverViewSectionData:
    | IRecommendedAndOverViewSection
    | undefined = bookLandingDetails
      ? extractRecommendedAndOverViewSectionData(bookLandingDetails)
      : undefined;

  const curriculumLandingPageData: ICurriculumLandingPageData | undefined =
    bookLandingDetails
      ? extractCurriculumLandingPageData(bookLandingDetails)
      : undefined;

  const sneakPeekData: ISneakPeek | undefined = bookLandingDetails
    ? extractSneakPeekData(bookLandingDetails)
    : undefined;

  const authorDetailsData: IAuthorDetails | IAuthorDetailsList[] | undefined =
    bookLandingDetails?.isMultipleAuthors
      ? bookLandingDetails?.authorsList
      : bookLandingDetails
        ? extractAuthorDetailsData(bookLandingDetails)
        : undefined;

  const iframeData: IIframeLandingPage | undefined = bookLandingDetails
    ? extractIframeData(bookLandingDetails)
    : undefined;

  const handleGetAuthorProfileById = async (zl_uid: string) => {
    try {
      const data = await getAuthorProfileById({ zl_uid });
      return {
        user_name: data?.data?.profile_data?.user_name,
        follower_count: data?.data?.follower_count,
        is_followed: data?.data?.is_followed,
        is_verified: data?.data?.is_verified,
      };
    } catch (error: any) {
      return null;
    }
  };

  const fetchAuthorData = async () => {
    if (
      heroSectionData?.isMultipleAuthors &&
      Array.isArray(heroSectionData?.authorsList)
    ) {
      const results = await Promise.all(
        heroSectionData.authorsList.map(
          (author) =>
            author.authorZlUid &&
            handleGetAuthorProfileById(author.authorZlUid),
        ),
      );
      const filtered = results.filter(Boolean) as IAuthorInterface[];
      setAuthorsData(filtered);
    } else if (heroSectionData?.authorZlUid) {
      const singleAuthor = await handleGetAuthorProfileById(
        heroSectionData.authorZlUid,
      );
      if (singleAuthor) {
        setAuthorsData(singleAuthor);
      }
    }
  };

  useEffect(() => {
    fetchAuthorData();
  }, [heroSectionData?.isMultipleAuthors, heroSectionData?.authorZlUid]);

  // Product Schema
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: metaData?.title || 'Default Product Name',
    brand: {
      '@type': 'Brand',
      name: 'ZebraLearn',
    },
    image: metaData?.image || 'https://zebralearn.com/logo.png',
    description: metaData?.description,
    category: 'Books',
    review: metaData?.reviews?.length
      ? metaData.reviews.map((r: any) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r?.user?.name ?? 'zebralearn' },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r?.rating ?? 4,
          bestRating: 5,
        },
        reviewBody: r.text,
      }))
      : undefined,
    aggregateRating:
      metaData?.reviews?.length && metaData.reviews.length > 0
        ? {
          '@type': 'AggregateRating',
          ratingValue: (
            metaData.reviews.reduce(
              (sum: number, r: any) => sum + (r?.rating ?? 4),
              0,
            ) / metaData.reviews.length
          ).toFixed(1),
          reviewCount: metaData.reviews.length,
          bestRating: Math.max(
            ...(metaData.reviews.map((r: any) => r?.rating ?? 4) || [5]),
          ),
        }
        : undefined,
  };

  // details_view Events
  useEffect(() => {
    if (!userData) return;
    EventV2({
      payload: {
        event_name: 'details_view',
        source: 'books',
        source_type: 'paperback',
        source_id: landingPageData?.form?.readersProductId,
        source_name: ecomProductData?.display_name,
        unit: 'boolean',
        value: 1,
        meta: {
          product_category: landingPageData?.categories?.[0]?.name,
          book_total_pages: landingPageData?.form?.numberOfPages,
        },
      },
      userData: userData,
      pathname: router.pathname,
    });
  }, [userData]);

  // details_session event — tracks active seconds, fires automatically when user leaves
  useSessionTracking({
    enabled: !!userData,
    userData,
    payload: {
      event_name: 'details_session',
      source: 'books',
      source_type: 'paperback',
      source_id: landingPageData?.form?.readersProductId,
      source_name: ecomProductData?.display_name,
      meta: {
        product_category: landingPageData?.categories?.[0]?.name,
        book_total_pages: landingPageData?.form?.numberOfPages,
      },
    },
  });

  const claimSoftButton = (
    <ButtonDefault
      disabled={purchasedData?.softProduct?.purchased || keyCount === 0}
      variant={ButtonVariants.PRIMARY}
      is3D
      size={0}
      className='w-full px-3 py-[9px]'
      onClick={() =>
        bookLandingDetails && claimHandle(bookLandingDetails?.digitalProductId)
      }
    >
      <p className='text-sm leading-[22px] font-semibold text-white '>
        Claim Digital Copy with 1
      </p>
      <WhiteKeyIcon />
    </ButtonDefault>
  );

  const claimHardButton = (
    <ButtonDefault
      disabled={
        purchasedData?.hardProduct?.purchased ||
        keyCount === 0 ||
        bookLandingDetails?.hardCoverAvailable === false
      }
      size={0}
      variant={ButtonVariants.PRIMARY}
      is3D
      className='w-full px-3 py-[9px] disabled:bg-black'
      onClick={() =>
        bookLandingDetails &&
        claimHandle(bookLandingDetails?.hardCoverProductId)
      }
    >
      <p className='text-sm leading-[22px] font-semibold text-white '>
        Claim Hard Copy with 1
      </p>
      <WhiteKeyIcon />
    </ButtonDefault>
  );

  const renderCTASection = () => {
    const bookType = bookLandingDetails?.bookType;
    const isShort = bookType === 'short';
    const isComingSoon = bookType === 'comingSoon';
    const isLong = !isShort && !isComingSoon;

    const hasDigitalPair = Boolean(ecomProductData?.digital_pair);
    const digitalId = Number(
      ecomProductData?.digital_pair?.id ||
        bookLandingDetails?.digitalProductId,
    );
    const formatBookTypeTitle = (type?: string) => {
      if (!type) {
        return bookLandingDetails?.bookType === 'paperBack' ? 'Paperback' : 'Hardcopy';
      }
      const normalized = type.trim().toLowerCase();
      if (normalized === 'hardcover' || normalized === 'hard_cover') {
        return 'Hardcopy';
      }
      return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const mainButtonTitle = formatBookTypeTitle(ecomProductData?.book_type);

    const effectiveDigitalCopyData =
      digitalCopyCountryData && digitalCopyCountryData.is_available !== null
        ? digitalCopyCountryData
        : ecomProductData?.digital_pair
        ? {
            stroked_price: formatPrice(ecomProductData.digital_pair.stroked_price),
            main_price: formatPrice(ecomProductData.digital_pair.main_price),
            currency_symbol: ecomProductData.digital_pair.currency_symbol || '₹',
            is_available:
              (ecomProductData.digital_pair.is_available &&
                ecomProductData.digital_pair.i18n_attributes) ||
              ecomProductData.digital_pair.is_available ||
              null,
            i18n_attributes: ecomProductData.digital_pair.i18n_attributes,
          }
        : digitalCopyCountryData;

    const claimWithGemsButton = (
      <>
        {isSubscriptionActive(userData) ? (
          <div className='mt-2.5'>
            {userData?.subscription?.snapshot?.rules?.[0]?.physical ? (
              purchasedData.hardProduct.purchased ? (
                React.cloneElement(claimHardButton, {
                  onClick: () => {
                    notify('Already Purchased', 'info');
                  },
                  disabled: false,
                  className: clsx(
                    claimHardButton.props.className,
                    'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                  ),
                })
              ) : keyCount !== 0 ? (
                claimHardButton
              ) : (
                React.cloneElement(claimHardButton, {
                  onClick: () => {
                    notify('Insufficient Credit', 'info');
                  },
                  disabled: false,
                  className: clsx(
                    claimHardButton.props.className,
                    'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                  ),
                })
              )
            ) : userData?.subscription?.snapshot?.rules?.[0]?.digital ? (
              purchasedData.softProduct.purchased ? (
                React.cloneElement(claimSoftButton, {
                  onClick: () => {
                    notify('Already Purchased', 'info');
                  },
                  disabled: false,
                  className: clsx(
                    claimSoftButton.props.className,
                    'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                  ),
                })
              ) : keyCount ? (
                claimSoftButton
              ) : (
                React.cloneElement(claimSoftButton, {
                  onClick: () => {
                    notify('Insufficient Credit', 'info');
                  },
                  disabled: false,
                  className: clsx(
                    claimSoftButton.props.className,
                    'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                  ),
                })
              )
            ) : (
              <></>
            )}
          </div>
        ) : null}

        <ShouldRender
          check={
            !isSubscriptionActive(userData) && hasPreviouslySubscribed(userData)
          }
        >
          {React.cloneElement(claimHardButton, {
            onClick: () => {
              notify('Resubscribe Now', 'info');
            },
            disabled: false,
            className: twMerge(
              claimHardButton.props.className,
              'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)] mt-2.5',
            ),
          })}
        </ShouldRender>
      </>
    );

    switch (true) {
      // case isPurchased:
      //   return (
      //     <div className='flex gap-2 w-full'>
      //       <ButtonDefault
      //         size={4}
      //         rounded={false}
      //         roundedSm={true}
      //         onClick={() => {
      //           router.push(
      //             `/pdf/${slidBookContainerData?.readersProductId}?chapterPageNumber=${lastPageRead || 1}`,
      //           );
      //         }}
      //         variant={ButtonVariants.WHITE}
      //         className='flex w-full justify-center px-5 py-3 border-none font-medium text-xs whitespace-nowrap'
      //       >
      //         <span className='px-4 text-sm md:text-base font-semibold py-1'>
      //           {lastPageRead === null || +lastPageRead === 1
      //             ? 'Start'
      //             : 'Continue'}{' '}
      //           Reading
      //         </span>
      //       </ButtonDefault>
      //     </div>
      //   );
      case isShort:
        return (
          <div className='flex gap-2 w-full'>
            <ButtonDefault
              size={4}
              rounded={false}
              roundedSm={true}
              onClick={() => {
                if (token) {
                  router.push(
                    `/pdf/${slidBookContainerData?.readersProductId}?samplePdf=true`,
                  );
                } else {
                  setVisible((prevState) => ({
                    ...prevState,
                    signUp: true,
                    bookId: slidBookContainerData?.readersProductId as string,
                  }));
                }
              }}
              variant={ButtonVariants.WHITE}
              className='flex w-full justify-center px-5 py-3 border-none font-medium text-xs whitespace-nowrap'
            >
              <span className='px-4 text-sm md:text-base font-semibold py-1'>
                Start Reading
              </span>
            </ButtonDefault>
          </div>
        );

      case isComingSoon:
        return (
          <div className='flex flex-col w-full justify-center'>
            <ButtonDefault
              isWidthFull={true}
              size={4}
              variant={
                isPreOrderAvailable
                  ? ButtonVariants.PRIMARY
                  : ButtonVariants.UNSTYLED
              }
              is3D={isPreOrderAvailable}
              unavailableText='Pre-Order'
              available={isPreOrderAvailable}
              loading={loadingAddToCart.value}
              disabled={loadingAddToCart.value}
              onClick={(e) => {
                e.stopPropagation();
                handleAddDataToCart(
                  {
                    id: Number(heroSectionData?.hardCoverProductId) as number,
                    temp_user_id: localUserId,
                    quantity: 1,
                    isBuyNow: true,
                    trackingData: {
                      productData: {
                        price: Number(
                          heroSectionData?.hardCopyPrice?.discountedPrice,
                        ),
                        id: Number(heroSectionData?.hardCoverProductId),
                        heading: `${ecomProductData?.display_name}`,
                        quantity: 1 as number,
                        currency: hardCopyCountryData?.i18n_attributes?.currency,
                        category: landingPageData?.categories?.[0]?.name,
                      },
                      userData,
                      pathname: router.pathname,
                      productType: 'hardcopy',
                      type: 'books',
                    },
                  },
                  'hardCopy',
                  true,
                );
              }}
              className='w-full p-0 py-0 px-0'
            >
              <div className='w-full flex justify-center font-semibold leading-6 items-center gap-1 py-[3px] px-3.5'>
                Pre-Order at {hardCopyCountryData?.currency_symbol}
                {hardCopyCountryData?.preorder_display_price}
              </div>
            </ButtonDefault>
            {claimWithGemsButton}
          </div>
        );

      case isLong:
      default:
        return (
          <>
            {/* <ButtonDefault
                      size={screens.md ? 4 : 1}
                      isWidthFull={true}
                      rounded={false}
                      roundedSm={true}
                      variant={ButtonVariants.WHITE}
                      enableScaling={false}
                      loading={
                        Number(bookLandingDetails?.digitalProductId) ===
                          loadingAddToCart.id && loadingAddToCart.value
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDataToCart(
                          {
                            id: Number(
                              bookLandingDetails?.digitalProductId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                          },
                          'digitalCopy',
                        );
  

                      className='w-full text-center text-sm group '
                    >
                      <div className='flex justify-between items-center w-full '>
                        <span className='flex flex-col justify-start'>
                          <p className='w-full text-start font-semibold text-[#737373] text-[10px]'>
                            Digital copy
                          </p>
                          <span className='flex gap-x-1 items-baseline pl-1'>
                            <p className='text-sm font-semibold whitespace-nowrap font-sans'>
                              {'\u20B9'}&nbsp;
                              {bookLandingDetails?.softCopyPrice?.discountedPrice}
                            </p>
                            <p className='line-through text-[#AEAEAE] font-sans text-[13px] font-semibold whitespace-nowrap'>
                              ₹ {bookLandingDetails?.softCopyPrice?.mrpPrice}
                            </p>
                          </span>
                        </span>
                        <span className=''>
                          <ArrowRightIcon className='w-4 md:w-5 text-[#AEAEAE] transform transition-transform duration-300 ease-in-out group-hover:-rotate-[20deg]' />
                        </span>
                      </div>
                    </ButtonDefault>
                    <ShouldRender
                      check={bookLandingDetails?.hardCoverAvailable !== false}
                    >
                      <ButtonDefault
                        size={screens.md ? 4 : 1}
                        rounded={false}
                        isWidthFull={true}
                        roundedSm={true}
                        loading={
                          Number(bookLandingDetails?.hardCoverProductId) ===
                            loadingAddToCart.id && loadingAddToCart.value
                        }
                        variant={ButtonVariants.PRIMARY}
                        enableScaling={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddDataToCart(
                            {
                              id: Number(
                                bookLandingDetails?.hardCoverProductId,
                              ) as number,
                              temp_user_id: localUserId,
                              quantity: 1,
                            },
                            'hardCopy',
                          );
  
                        className='w-full text-center text-sm   group'
                      >
                        <div className='flex justify-between items-center w-full'>
                          <span className='flex flex-col justify-start'>
                            <p className='w-full text-start font-semibold  text-[10px]'>
                              Hard copy + Digital copy
                            </p>
                            <span className='flex gap-x-2 items-baseline pl-1'>
                              <p className='text-sm font-semibold whitespace-nowrap font-sans'>
                                ₹{' '}
                                {
                                  bookLandingDetails?.hardCopyPrice
                                    ?.discountedPrice
                                }
                              </p>
                              <p className='line-through text-[#8A2800] text-[13px] font-sans font-semibold whitespace-nowrap'>
                                ₹ {bookLandingDetails?.hardCopyPrice?.mrpPrice}
                              </p>
                            </span>
                          </span>
                          <span>
                            <ArrowRightIcon className='w-4 md:w-5 transform transition-transform duration-300 ease-in-out group-hover:-rotate-[20deg] text-[#8A2800]' />
                          </span>
                        </div>
                      </ButtonDefault>
                    </ShouldRender>
                    <ShouldRender
                      check={bookLandingDetails?.hardCoverAvailable === false}
                    >
                      <Button
                        size='small'
                        type='text'
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: '#141414',
  
                          border: 'none',
                          borderRadius: '8px',
                          padding: '5px 15px',
                          zIndex: 1,
                          boxShadow: '0px 4px 4px 0px #0000004D',
                        }}
                        className='w-full h-full min-h-[62px] hover:text-white text-xs font-semibold'
                      >
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: -1,
                            borderRadius: 'inherit',
                            padding: '1.3px',
                            background:
                              'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                            WebkitMask:
                              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                          }}
                        />
                        <div className='flex justify-center items-center w-full '>
                          <span className='flex flex-col justify-start gap-y-1.5'>
                            <p className='w-full text-center font-semibold text-white text-[10px]'>
                              Hard Copy
                            </p>
                            <span
                              className='flex gap-x-2 font-bold text-xs items-baseline pl-1'
                              style={{
                                background:
                                  'linear-gradient(to bottom, #6199FF, #045CFB)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              Available SOON
                            </span>
                          </span>
                        </div>
                      </Button>
                    </ShouldRender> */}

            {/* Start */}
            <div className='flex flex-col w-full'>
              <div
                className={twMerge(
                  'grid grid-cols-2 gap-2.5 w-full',
                  !hasDigitalPair ? 'grid-cols-1' : '',
                )}
              >
                <div className='relative min-w-[151px] flex flex-1 rounded-lg h-full'>
                  {/* Hard Copy Button */}
                  <ShouldRender
                    check={bookLandingDetails?.hardCoverAvailable !== false}
                  >
                    <ButtonDefault
                      size={0}
                      isWidthFull={true}
                      rounded={false}
                      roundedSm={true}
                      variant={
                        hardCopyCountryData?.is_available !== null
                          ? ButtonVariants.WHITE
                          : ButtonVariants.UNSTYLED
                      }
                      available={hardCopyCountryData?.is_available !== null}
                      enableScaling={false}
                      loading={
                        Number(bookLandingDetails?.hardCoverProductId) ===
                        loadingAddToCart.id && loadingAddToCart.value
                      }
                      disabled={
                        Number(bookLandingDetails?.hardCoverProductId) ===
                        loadingAddToCart.id && loadingAddToCart.value
                      }
                      unavailableText='Hard Copy'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDataToCart(
                          {
                            id: Number(
                              bookLandingDetails?.hardCoverProductId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                            trackingData: {
                              productData: {
                                price: Number(
                                  bookLandingDetails?.hardCopyPrice
                                    ?.discountedPrice,
                                ),
                                id: Number(bookLandingDetails?.hardCoverProductId),
                                heading: `${ecomProductData?.display_name}`,
                                quantity: 1 as number,
                                currency:
                                  hardCopyCountryData?.i18n_attributes?.currency,
                                category: landingPageData?.categories?.[0]?.name,
                              },
                              userData,
                              pathname: router.pathname,
                              productType: 'hardcopy',
                              type: 'books',
                            },
                          },
                          'hardCopy',
                        );
                      }}
                      className={clsx(
                        hasDigitalPair &&
                        'border-none',
                        'px-3 py-2',
                      )}
                    >
                      <div className='active:scale-95 transition-transform cursor-pointer relative flex-1 flex flex-col gap-1 bg-white rounded-lg'>
                        <div className='flex items-center justify-center gap-2.5 '>
                          <p className='text-sm text-center font-semibold text-black line-clamp-1'>
                            <span className='capitalize'>
                              {mainButtonTitle}
                            </span>
                            {hasDigitalPair && ' + Digital'}
                          </p>
                        </div>
                        <div className='flex items-center gap-1 justify-center'>
                          <p className='text-base/6 font-semibold text-black'>
                            {hardCopyCountryData?.currency_symbol}
                            {hardCopyCountryData?.main_price}
                          </p>
                          <p className='text-sm/6 font-medium text-grayZl-700 line-through'>
                            {hardCopyCountryData?.currency_symbol}{' '}
                            {hardCopyCountryData?.stroked_price}
                          </p>
                        </div>
                      </div>
                    </ButtonDefault>
                  </ShouldRender>

                  {/* Avaiable Soon */}
                  <ShouldRender
                    check={bookLandingDetails?.hardCoverAvailable === false}
                  >
                    <div className='w-full bg-black rounded-lg border-[1.5px] border-grayZl-850 h-full flex flex-col items-center justify-center'>
                      <p className='text-xs/5 font-semibold text-center'>
                        Hard Copy
                      </p>
                      <p className='text-xs/5 font-medium  text-transparent bg-[linear-gradient(180deg,#F9742C_0%,#F20486_100%)] bg-clip-text'>
                        Available Soon
                      </p>
                    </div>
                  </ShouldRender>
                </div>
                <ShouldRender
                  check={hasDigitalPair}
                >
                  {/* Soft Copy Button */}
                  <ButtonDefault
                    size={0}
                    isWidthFull={true}
                    rounded={false}
                    roundedSm={true}
                    variant={
                      effectiveDigitalCopyData?.is_available !== null
                        ? ButtonVariants.GRAY
                        : ButtonVariants.UNSTYLED
                    }
                    available={effectiveDigitalCopyData?.is_available !== null}
                    enableScaling={false}
                    loading={
                      Number(digitalId) ===
                      loadingAddToCart.id && loadingAddToCart.value
                    }
                    disabled={
                      Number(digitalId) ===
                      loadingAddToCart.id && loadingAddToCart.value
                    }
                    unavailableText='Digital Copy'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPlatformSubscriptionActive(userData)) return;
                      handleAddDataToCart(
                        {
                          id: Number(
                            digitalId,
                          ) as number,
                          temp_user_id: localUserId,
                          quantity: 1,
                          trackingData: {
                            productData: {
                              price: Number(
                                effectiveDigitalCopyData?.main_price ||
                                ecomProductData?.digital_pair?.main_price ||
                                bookLandingDetails?.softCopyPrice?.discountedPrice,
                              ),
                              id: Number(digitalId),
                              heading: `${ecomProductData?.digital_pair?.display_name || ecomProductData?.display_name}`,
                              quantity: 1 as number,
                              currency:
                                effectiveDigitalCopyData?.currency_symbol ||
                                hardCopyCountryData?.i18n_attributes?.currency,
                              category: landingPageData?.categories?.[0]?.name,
                            },
                            userData,
                            pathname: router.pathname,
                            productType: 'digitalcopy',
                            type: 'books',
                          },
                        },
                        'digitalCopy',
                      );
                    }}
                    className={clsx(
                      effectiveDigitalCopyData?.is_available !== null &&
                      'border-none',
                      'min-h-[66px]  px-3 py-2',
                    )}
                  >
                    <div className='active:scale-95 transition-transform flex-1 flex flex-col gap-1 rounded-lg'>
                      <div className='flex items-center justify-center gap-2.5'>
                        <p className='text-sm text-center font-semibold text-white'>
                          Digital Copy
                        </p>
                      </div>
                      {isPlatformSubscriptionActive(userData) ? (
                        <p className='text-start text-sm/6 font-medium text-error-400 whitespace-nowrap'>
                          Already Subscribed
                        </p>
                      ) : (
                        <div className='flex items-center gap-1 justify-center'>
                          <p className='text-base/6 font-semibold text-white'>
                            {effectiveDigitalCopyData?.currency_symbol || '₹'}
                            {effectiveDigitalCopyData?.main_price}
                          </p>
                          <p className='text-sm/6 font-medium text-grayZl-700 line-through'>
                            {effectiveDigitalCopyData?.currency_symbol || '₹'}{' '}
                            {effectiveDigitalCopyData?.stroked_price}
                          </p>
                        </div>
                      )}
                    </div>
                  </ButtonDefault>
                </ShouldRender>
              </div>
              <ShouldRender
                check={hasDigitalPair}
              >
                {claimWithGemsButton}
              </ShouldRender>

              {/* <div className='text-white flex gap-x-2 text-xs/5 font-normal justify-center my-0.5'>
                        <SecurePaymentV2 /> Secure Payments
                      </div> */}
            </div>
            {/* End */}
          </>
        );
    }
  };

  return (
    <>
      <Head>
        <title key='title'>
          {metaData?.title || 'Book Details - ZebraLearn'}
        </title>
        <meta
          name='description'
          content={
            metaData?.description || 'Explore our latest books at ZebraLearn.'
          }
          key='description'
        />
        <meta
          name='keywords'
          content={metaData?.keywords || 'books, learning, education'}
          key='keywords'
        />
        <meta
          name='author'
          content={metaData?.author || 'ZebraLearn'}
          key='author'
        />

        <meta
          property='og:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='og-title'
        />
        <meta
          property='og:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='og-description'
        />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
          }
          key='og-image'
        />

        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        {/* Twitter Metadata (Overrides Global) */}
        <meta
          name='twitter:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='twitter-title'
        />
        <meta
          name='twitter:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          }
          key='twitter-image'
        />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta
          property='article:section'
          content='Website Section'
          key='article-section'
        />
        <meta
          property='article:tag'
          content={metaData?.keywords as string}
          key='article-tag'
        />
        <link
          rel='canonical'
          href={removeTrailingSlash(
            metaData?.canonicalUrl ?? (canonicalUrlPageUrl as string),
          )}
          key='canonical'
        />
      </Head>

      <Script
        id='product-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ShouldRender
        check={!loading && bookLandingDetails && swp?.toString() !== 'false'}
      >
        <div className=' relative w-full '>
          <ShouldRender
            check={
              bookLandingDetails?.bookType === 'comingSoon' &&
              (getGlobalCountry()?.name?.toLowerCase() === 'india' ||
                isPreOrderAvailable)
            }
          >
            <div className='w-full fixed top-[55px] lg:top-[60px] left-0 bg-gradient-to-r from-black via-[#1a1a1a] to-[#444] text-white text-sm z-30'>
              <div className='max-w-[1240px] mx-auto flex justify-center items-center py-3 md:py-[18px]'>
                <span className='opacity-80 hidden md:inline-block'>
                  Pre-order offer
                </span>
                <span className='opacity-80'>
                  <span className='hidden md:inline-block px-2'>|</span>
                  Get Hard + Digital Book at{' '}
                  <span className='text-[#FF5B00] font-medium'>
                    {' '}
                    {hardCopyCountryData?.currency_symbol}
                    {hardCopyCountryData?.preorder_display_price}
                  </span>{' '}
                  only
                </span>
              </div>
            </div>
          </ShouldRender>
          <div
            className={clsx(
              bookLandingDetails?.bookType !== 'comingSoon' ? ' ' : 'mt-24',
              ' relative flex h-full ',
            )}
          >
            <div className='w-full pb-20 md:pb-24 lg:pb-5'>
              <Section direction='left'>
                <div>
                  <ShouldRender check={!screens.lg}>
                    <Section>
                      <div className='h-full z-40'>
                        <SlidBookContainer
                          ecomProductData={ecomProductData}
                          productAccessCheckData={productAccessCheckData}
                          bookLandingDetails={bookLandingDetails}
                          purchasedData={purchasedData}
                          slidBookContainerData={
                            slidBookContainerData as ISlidBook
                          }
                          token={token}
                          digitalCopyCountryData={digitalCopyCountryData}
                          hardCopyCountryData={hardCopyCountryData}
                          isPurchased={isPurchased as boolean}
                          lastPageRead={lastPageRead as number}
                          landingPageData={landingPageData}
                          isOrange={isOrange}
                        />
                      </div>
                    </Section>
                  </ShouldRender>
                  <div className=' bg-bgColor lg:bg-transparent md:py-4 lg:pt-[56px] lg:py-[20px] px-4  md:px-10 xl:px-20 relative overflow-hidden'>
                    <div className='absolute customGradient inset-0 hidden md:block   translate-y-2 backdrop-blur-xl -z-10'></div>
                    <div className='absolute inset-0 hidden lg:block  bg-[#000000CC]  -z-20'></div>

                    <div className='absolute inset-0 hidden lg:block  w-full h-full -z-30'>
                      <Image
                        src={heroSectionData?.bookImage as string}
                        alt='Background Image'
                        layout='fill'
                        priority
                        className='rotate-90    transform w-full h-auto absolute -translate-y-10 2xl:-translate-y-24 top-0 left-0'
                      />
                    </div>

                    <ShouldRender check={!loading && heroSectionData}>
                      <div className='z-40 '>
                        <HeroSection
                          ecomProductData={ecomProductData}
                          authorsData={
                            authorsData as IAuthorInterface | IAuthorInterface[]
                          }
                          heroSectionData={
                            heroSectionData as IHeroSectionLandingPage
                          }
                          digitalCopyCountryData={digitalCopyCountryData}
                          hardCopyCountryData={hardCopyCountryData}
                          isOrange={isOrange}
                          landingPageData={landingPageData}
                        />
                      </div>
                    </ShouldRender>
                  </div>

                  <div className='lg:w-1/2 xl:w-2/3 pt-[56px] pb-[20px] md:py-[20px] px-5 md:px-10 lg:pl-10 lg:pr-0 xl:px-20 text-white flex flex-col gap-y-[40px]'>
                    <BookDescription
                      isOrange={isOrange}
                      description={
                        bookDescriptionData?.bookDescription as string[]
                      }
                      whatYouWillLearn={
                        bookDescriptionData?.whatYouWillLearn as string[]
                      }
                    />
                    <GenuineProduct isOrange={isOrange} />
                    <PublicationDetails isOrange={isOrange} landingPageData={landingPageData} />
                    <ShouldRender check={!short}>
                      <ShouldRender
                        check={bookLandingDetails?.bookType !== 'comingSoon'}
                      >
                        <IframeLandingPage
                          data={iframeData as IIframeLandingPage}
                          isOrange={isOrange}
                        />
                        <Banner />
                        <RecommendedAndOverViewSection
                          recommendedAndOverViewSectionData={
                            recommendedAndOverViewSectionData as IRecommendedAndOverViewSection
                          }
                          isOrange={isOrange}
                        />
                      </ShouldRender>
                    </ShouldRender>

                    <CurriculumSection
                      data={
                        curriculumLandingPageData as ICurriculumLandingPageData
                      }
                      isOrange={isOrange}
                    />

                    <SneakPeekSection
                      data={sneakPeekData?.sneakPeek as string[]}
                      isOrange={isOrange}
                    />
                    <AuthorDetailsSection
                      landingPageData={landingPageData}
                      data={
                        authorDetailsData as
                        | IAuthorDetails
                        | IAuthorDetailsList[]
                      }
                      authorsData={authorsData}
                      isMultipleAuthors={
                        heroSectionData?.isMultipleAuthors || false
                      }
                      productId={bookDetails?._id || ''}
                      productName={bookDetails?.bookName || ''}
                      isOrange={isOrange}
                    />

                    <ReviewsLandingPage
                      isLoggedIn={!!token}
                      digitalCoverId={
                        bookLandingDetails?.digitalProductId?.toString() as string
                      }
                      hardCoverId={
                        bookLandingDetails?.hardCoverProductId?.toString() as string
                      }
                      isComingSoon={
                        bookLandingDetails?.bookType === 'comingSoon'
                      }
                      isPurchased={isPurchased}
                      isOrange={isOrange}
                    />



                    <RecommendedBooksSection
                      category={
                        bookLandingDetails?.categories?.[0]?.name ?? undefined
                      }
                      categoryId={
                        bookLandingDetails?.categories?.[0]?.categoryId
                      }
                      token={token}
                      type={
                        bookLandingDetails?.bookType === 'comingSoon'
                          ? 'comingSoon'
                          : 'book'
                      }
                    />
                  </div>
                </div>
              </Section>
            </div>
            <ShouldRender check={screens?.lg}>
              <AnimatePresence mode='wait'>
                <div className='absolute right-20 2xl:right-[8%] top-[8px]'>
                  <Affix offsetTop={offset}>
                    <Section direction='right'>
                      <div className='slid-book-container transition-all duration-300 ease-in-out'>
                        <SlidBookContainer
                          ecomProductData={ecomProductData}
                          productAccessCheckData={productAccessCheckData}
                          bookLandingDetails={bookLandingDetails}
                          purchasedData={purchasedData}
                          slidBookContainerData={
                            slidBookContainerData as ISlidBook
                          }
                          token={token}
                          digitalCopyCountryData={digitalCopyCountryData}
                          hardCopyCountryData={hardCopyCountryData}
                          isPurchased={isPurchased}
                          lastPageRead={lastPageRead as number}
                          landingPageData={landingPageData}
                          isOrange={isOrange}

                        />
                      </div>
                    </Section>
                  </Affix>
                </div>
              </AnimatePresence>
            </ShouldRender>

            <ShouldRender check={!screens.lg}>
              <div
                className={clsx(
                  !screens.lg && 'fixed bottom-0 left-0 z-40',
                  'w-full',
                )}
              >
                <div className='bg-black border-t border-grayZl-850 px-4 xxs:px-4 md:px-10 lg:px-0 pt-5 pb-3 backdrop-blur-sm flex gap-x-2.5 xxs:gap-x-2.5 relative z-40'>
                  {renderCTASection()}
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>
      </ShouldRender>

      <ShouldRender check={swp?.toString() === 'false'}>
        <Section>
          <div id='products' className={` flex items-center w-full `}>
            <ShouldRender check={!loading}>
              <Products productData={bookDetails} token={token} />
            </ShouldRender>
          </div>
        </Section>
      </ShouldRender>

      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  resolvedUrl,
  req,
}) => {
  try {
    const { bookId } = query;
    const parts = (bookId as string).split('-');

    const rawCookies = req.headers.cookie || '';
    const parsedCookies = cookie.parse(rawCookies);

    const token = parsedCookies['auth_token']?.trim();
    const metaData = await getLandingPageSeoSSR(parts[parts.length - 1] || '');
    const landingPageData = await getBookLandingPageDataSSR(bookId as string);

    let country = parsedCookies['country']?.trim();

    if (!country) {
      const [activeCountries, detectedCode] = await Promise.all([
        getActiveCountries(),
        detectCountry(req),
      ]);

      if (activeCountries.length > 0) {
        const fallback = activeCountries.find((c: any) => c.code === 'IN');
        const found =
          activeCountries.find((c: any) => c.code === detectedCode) || fallback;

        country = found?.code || 'IN';
      }
    }

    let hardCopy = null;
    let digitalCopy = null;
    let ecomProductData = null;

    if (landingPageData?.form?.hardCoverProductId) {
      const productData = await getProductDataSSR(
        landingPageData?.form?.hardCoverProductId,
        country as string,
      );
      ecomProductData = productData;

      if (productData) {
        hardCopy = {
          stroked_price: formatPrice(productData?.stroked_price),
          main_price: formatPrice(productData?.main_price),
          currency_symbol: productData?.currency_symbol,
          is_available:
            (productData?.is_available && productData?.i18n_attributes) || null,
          is_preorder: productData?.is_preorder,
          i18n_attributes: productData?.i18n_attributes,
          preorder_price: productData?.preorder_price || null,
          preorder_display_price: productData?.preorder_display_price || null,
          preorder_coupon_code: productData?.preorder_coupon_code || null,
        };

        if (productData?.digital_pair) {
          digitalCopy = {
            stroked_price: formatPrice(
              productData?.digital_pair?.stroked_price,
            ),
            main_price: formatPrice(productData?.digital_pair?.main_price),
            currency_symbol: productData?.digital_pair?.currency_symbol,
            is_available:
              (productData?.digital_pair?.is_available &&
                productData?.digital_pair?.i18n_attributes) ||
              null,
            is_preorder: productData?.digital_pair?.is_preorder,
            i18n_attributes: productData?.digital_pair?.i18n_attributes,
            preorder_price: productData?.digital_pair?.preorder_price || null,
          };
        }
      }
    } else {
      console.warn('⚠️ No hardCoverProductId found in landingPageData.form');
    }

    let isPurchased = null;
    let lastPageRead = 1;
    if (token) {
      try {
        const bookPageData = await getBooDataByIdSSR(
          parts.slice(-1)[0] as string,
          token as string,
        );
        isPurchased = bookPageData?.isPurchased || false;
        lastPageRead = bookPageData?.pageRead?.pageNumber || null;
      } catch (error) {
        isPurchased = false;
      }
    }

    const formattedMetaData = {
      title: metaData?.title || '',
      description: metaData?.description || '',
      image:
        generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${metaData?._id}`,
        ) || '',
      author: landingPageData?.form?.authorName || '',
      keywords: metaData?.keywords || '',
      openGraph: {
        title: metaData?.openGraph?.title ?? null,
        description: metaData?.openGraph?.description ?? null,
        image: metaData?.openGraph?.image ?? null,
      },
      categories: landingPageData?.form?.categories ?? null,
      slug: landingPageData?.form?.categories?.slug ?? null,
      canonicalUrl: metaData?.canonicalUrl ?? null,
      price: landingPageData?.form?.hardCopyPrice?.discountedPrice ?? null,
      reviews: metaData?.reviews ?? [],
      ratingValue: landingPageData?.form?.rating ?? null,
      reviewCount: metaData?.reviews?.length ?? 0,
    };
    const canonicalUrlPageUrl = `https://zebralearn.com${resolvedUrl.split('?')[0]}`;

    let productAccessCheckData = null;
    if (token && landingPageData?.form?.digitalProductId) {
      try {
        const result = await productAccessCheckSSR(
          landingPageData.form.digitalProductId,
          token,
        );
        productAccessCheckData = result.data;
      } catch (error) {
        console.warn('Initial product access check failed:', error);
      }
    }

    return {
      props: {
        metaData: formattedMetaData,
        canonicalUrlPageUrl,
        digitalCopyCountryData: digitalCopy,
        hardCopyCountryData: hardCopy,
        isPurchased,
        lastPageRead,
        productAccessCheckData,
        landingPageData,
        ecomProductData,
      },
    };
  } catch (error: any) {
    console.error({ error });
    if (error?.code === 404 || error?.message?.includes('404')) {
      return {
        redirect: {
          destination: '/',
          permanent: false,
        },
      };
    }

    return {
      props: {
        metaData: null,
        canonicalUrlPageUrl: 'https://zebralearn.com/',
        digitalCopyCountryData: null,
        hardCopyCountryData: null,
        isPurchased: false,
        lastPageRead: 1,
        productAccessCheckData: null,
        landingPageData: null,
        ecomProductData: null,
      },
    };
  }
};

export default BookDetailsPage;
